import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Chip,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import type { PromptScope } from '../../../shared/types/prompts';
import { useGetProfileQuery } from '../../../store/api/rpcSlice';

export function promptSlotDisplay(slot: {
  slot: string;
  resolved_prompt?: { id?: string; slug: string; text: string; scope: PromptScope };
  resolved_source: string;
}) {
  return {
    label: slot.slot,
    slug: slot.resolved_prompt?.slug ?? 'none',
    text: slot.resolved_prompt?.text ?? 'No prompt resolved for this slot.',
    chipLabel: slot.resolved_source || 'none',
    isSelectable: Boolean(slot.resolved_prompt),
  };
}

interface ProfileCard {
  slug: string;
  base_prompt_slug: string;
  middleware_count: number;
}

interface BrowseColumnProps {
  profiles: ProfileCard[];
  onPromptSelected: (
    profileSlug: string,
    promptSlug: string,
    baseText: string,
    promptScope: PromptScope,
    promptId?: string | null
  ) => void;
  selectedPromptSlug: string | null;
}

export function BrowseColumn({
  profiles,
  onPromptSelected,
  selectedPromptSlug,
}: BrowseColumnProps) {
  const [selectedProfileSlug, setSelectedProfileSlug] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredProfiles = profiles.filter(
    (p) => !searchQuery || p.slug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Search */}
      <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search profiles..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiInputBase-root': { fontSize: 13 } }}
        />
      </Box>

      {/* Profile list + detail */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {!selectedProfileSlug ? (
          // Profile list
          <List dense disablePadding>
            {filteredProfiles.map((profile) => (
              <ListItemButton
                key={profile.slug}
                onClick={() => setSelectedProfileSlug(profile.slug)}
                sx={{ px: 2, py: 1 }}
              >
                <ListItemText
                  primary={profile.slug}
                  secondary={`${profile.middleware_count} middleware`}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500, fontSize: 13 }}
                  secondaryTypographyProps={{ variant: 'caption', fontSize: 11 }}
                />
              </ListItemButton>
            ))}
          </List>
        ) : (
          // Profile detail
          <Box>
            <Box
              onClick={() => setSelectedProfileSlug(null)}
              sx={{
                px: 2,
                py: 1,
                cursor: 'pointer',
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {'<'} All Profiles
              </Typography>
              <Typography variant="body2" fontWeight={600} fontSize={13}>
                {selectedProfileSlug}
              </Typography>
            </Box>
            <ProfileDetail
              profileSlug={selectedProfileSlug}
              onPromptSelected={onPromptSelected}
              selectedPromptSlug={selectedPromptSlug}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

function ProfileDetail({
  profileSlug,
  onPromptSelected,
  selectedPromptSlug,
}: {
  profileSlug: string;
  onPromptSelected: (
    profileSlug: string,
    promptSlug: string,
    baseText: string,
    promptScope: PromptScope,
    promptId?: string | null
  ) => void;
  selectedPromptSlug: string | null;
}) {
  const { data, isLoading } = useGetProfileQuery({ slug: profileSlug });

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  const profile = data?.profile;
  if (!profile) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Profile not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1.5 }}>
      {/* Base prompt */}
      {profile.base_prompt && (
        <PromptSlotCard
          label="Base Prompt"
          slug={profile.base_prompt.slug}
          text={profile.base_prompt.text}
          chipLabel="Main"
          chipColor="success"
          isSelected={selectedPromptSlug === profile.base_prompt.slug}
          onClick={() =>
            onPromptSelected(
              profileSlug,
              profile.base_prompt?.slug ?? '',
              profile.base_prompt?.text ?? '',
              profile.base_prompt?.scope ?? { type: 'global' },
              profile.base_prompt?.id
            )
          }
        />
      )}

      <Divider sx={{ my: 1 }} />
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ px: 0.5 }}>
        Middleware Stack
      </Typography>

      {profile.middleware_prompt_views?.map(
        (mw: {
          name: string;
          slots: Array<{
            slot: string;
            resolved_prompt?: { id?: string; slug: string; text: string; scope: PromptScope };
            resolved_source: string;
          }>;
        }) => (
          <Box key={mw.name} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, fontWeight: 500 }}>
              {mw.name}
            </Typography>
            {mw.slots?.map((slot) => {
              const display = promptSlotDisplay(slot);
              const resolvedPrompt = slot.resolved_prompt;
              return (
                <PromptSlotCard
                  key={slot.slot}
                  label={display.label}
                  slug={display.slug}
                  text={display.text}
                  chipLabel={display.chipLabel}
                  chipColor={slot.resolved_source === 'override' ? 'secondary' : 'default'}
                  isSelected={selectedPromptSlug === resolvedPrompt?.slug}
                  onClick={
                    resolvedPrompt
                      ? () =>
                          onPromptSelected(
                            profileSlug,
                            resolvedPrompt.slug,
                            resolvedPrompt.text,
                            resolvedPrompt.scope,
                            resolvedPrompt.id
                          )
                      : undefined
                  }
                />
              );
            })}
          </Box>
        )
      )}
    </Box>
  );
}

export function PromptSlotCard({
  label,
  slug,
  text,
  chipLabel,
  chipColor,
  isSelected,
  onClick,
}: {
  label: string;
  slug: string;
  text: string;
  chipLabel: string;
  chipColor: 'success' | 'secondary' | 'default';
  isSelected: boolean;
  onClick?: () => void;
}) {
  return (
    <Box
      onClick={onClick}
      sx={{
        p: 1,
        mt: 0.5,
        borderRadius: 1,
        border: '2px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        bgcolor: isSelected ? 'primary.50' : 'transparent',
        cursor: onClick ? 'pointer' : 'default',
        opacity: onClick ? 1 : 0.72,
        transition: 'all 150ms',
        '&:hover': {
          borderColor: onClick ? (isSelected ? 'primary.main' : 'primary.light') : 'divider',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <Typography variant="caption" fontWeight={600} fontSize={11}>
          {label}
        </Typography>
        <Chip
          label={chipLabel}
          size="small"
          color={chipColor}
          variant="outlined"
          sx={{ height: 18, '& .MuiChip-label': { fontSize: 10, px: 0.5 } }}
        />
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', fontSize: 10, lineHeight: 1.2, mb: 0.25 }}
      >
        {slug}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', fontSize: 11, lineHeight: 1.3 }}
      >
        {text.length > 80 ? `${text.slice(0, 80)}...` : text}
      </Typography>
    </Box>
  );
}
